<template>
  <div>
    <label
      v-if="label"
      :for="id"
      class="mb-1 block text-left text-sm font-medium text-background-fg/70"
    >
      {{ label }}
    </label>
    <div class="relative mt-1 rounded-md shadow-sm">
      <input
        :id="id"
        :type="type_"
        :name="name"
        :class="[
          'block w-full rounded-md border border-background-fg/8 pr-10 text-sm font-semibold focus:outline-none sm:text-sm',
          hasError
            ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
            : 'focus:border-primary focus:ring-primary',
        ]"
        :placeholder="placeholder"
        :value="inputValue"
        :aria-invalid="hasError"
        enterkeyhint="done"
        :disabled="disabled"
        v-bind="extraInputAttributes"
        :autocomplete="
          type === 'pin' ? 'off' : autocomplete
        "
        @input="handleChange"
        @blur="handleBlur"
      />
      <div
        class="absolute inset-y-0 right-0 flex items-center pr-3"
      >
        <button
          type="button"
          class="flex items-center"
          tabindex="-1"
          @click="toggleVisible"
        >
          <template v-if="isVisible">
            <IconHeroiconsOutlineEye />
          </template>
          <template v-else>
            <IconHeroiconsOutlineEyeOff />
          </template>
        </button>
      </div>
    </div>
    <template v-if="hasError">
      <p class="mt-1 text-xs font-medium text-error">
        {{ errorMessage }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useField } from "vee-validate"
import type { InputHTMLAttributes } from "vue"

import { useId } from "vue"

const {
  modelValue = "",
  name,
  label,
  placeholder = "",
  disabled = false,
  type = "password",
  autocomplete = "off",
} = defineProps<{
  modelValue?: string
  name: string
  label: string
  placeholder?: string
  disabled?: boolean
  type?: "password" | "pin"
  autocomplete?: string
}>()

const id = useId()

type InputType = "password" | "text"

const type_ = computed<InputType>(() =>
  isVisible.value ? "text" : "password"
)
const isVisible = ref(false)
const toggleVisible = () => {
  isVisible.value = !isVisible.value
}
const {
  value: inputValue,
  errorMessage,
  handleBlur,
  handleChange,
} = useField(name, undefined, {
  initialValue: modelValue,
})

const hasError = computed(() => !!errorMessage.value)

const extraInputAttributes = computed<InputHTMLAttributes>(
  () =>
    type === "pin"
      ? { pattern: "[0-9]*", inputmode: "numeric" }
      : {}
)
</script>
